import { z } from 'zod'

/**
 * De twee primitieven die door de hele runtimehelft van het model lopen.
 */

/**
 * Een tijdstip, altijd in UTC met een Z erachter, zoals in ontwerp 4.5
 * (`2026-11-04T14:22:31Z`). Een zone-aanduiding toestaan zou betekenen dat twee
 * revisies uit dezelfde beurt niet betrouwbaar te ordenen zijn, en de ordening
 * van revisies is precies waar R-95 op rust.
 */
export const Moment = z
  .string()
  .regex(
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/,
    'verwacht een tijdstip in UTC, zoals 2026-11-04T14:22:31Z',
  )
export type Moment = z.infer<typeof Moment>

/**
 * Wie iets deed: de ingang waarlangs iemand inlogde, met de vaste sleutel van de persoon
 * erachter, zoals `portaal:00000000-0000-4000-8000-000000000001` (R-155e, ontwerp 3.3 en B18).
 * Intern is die sleutel de object-id uit Entra ID. Tot de vaste sleutel was het het subject
 * uit het token (ontwerp 4.5, brief B4), en opgeslagen personen van publicaties van daarvoor
 * houden die vorm: ze worden niet omgezet.
 *
 * Het product heeft geen eigen gebruikerstabel (R-154). Er staat hier dus geen
 * naam en geen e-mailadres: alleen de verwijzing die de gastheer uitgeeft. Dat is
 * ook de reden dat een revisie `persoon` en `namens` los vastlegt (R-191) in
 * plaats van een gebruikersrecord te kopiëren.
 */
export const Person = z
  .string()
  .regex(
    /^[a-z][a-z0-9-]{0,31}:[A-Za-z0-9._~-]{1,128}$/,
    'verwacht een ingang met de vaste sleutel van de persoon erachter, zoals portaal:00000000-0000-4000-8000-000000000001',
  )
export type Person = z.infer<typeof Person>

/** De vaste breedte van datum en tijd tot op de seconde: `2026-11-04T14:22:31`. */
const SECONDS_WIDTH = 19

/**
 * Twee momenten in de tijd vergeleken: negatief als `a` eerder is, nul bij hetzelfde moment,
 * positief als `a` later is.
 *
 * Nooit als tekst vergelijken. De opslagdienst schrijft een moment op een hele seconde in de
 * korte vorm (`2026-10-28T18:38:56Z`, zie `toMoment` in de store) en een ander met
 * milliseconden, en als tekst sorteert `.` voor `Z`. Dan komt `…:56.412Z` voor `…:56Z`, en zo
 * weigerde de vorm een deling die in dezelfde seconde werd ingetrokken als waarin ze op een hele
 * seconde ontstond: ongeveer een op de duizend keer, en dus een wankele test.
 *
 * Ook niet via `Date.parse`: `Moment` staat tot negen decimalen toe en de taalnorm belooft er
 * maar drie, terwijl dit pakket in Node en in de browser hetzelfde moet uitrekenen (ontwerp 5.9).
 * Datum en tijd hebben in `Moment` een vaste breedte; met de fractie aangevuld tot negen cijfers
 * zijn twee momenten dus als tekst te vergelijken zonder iets weg te ronden.
 *
 * Is een van de twee geen moment, dan is de uitkomst `NaN` en geen exceptie: elke vergelijking
 * ermee is dan onwaar. Dat is met opzet. Deze functie draait in de `superRefine` van een schema,
 * en Zod voert die ook uit als een veld al op zijn eigen patroon is gevallen. Een exceptie zou
 * daar uit `safeParse` lekken in plaats van een gewone weigering te geven, en een `Thread` komt
 * uit het sectiedocument, waar een fout in de render inhoud kan kosten (ontwerp bijlage C.4). Het
 * veld meldt zijn eigen fout al.
 */
export function compareMoments(a: Moment, b: Moment): number {
  const left = sortable(a)
  const right = sortable(b)
  if (left === undefined || right === undefined) return Number.NaN
  return left < right ? -1 : left > right ? 1 : 0
}

function sortable(moment: Moment): string | undefined {
  if (!Moment.safeParse(moment).success) return undefined
  const seconds = moment.slice(0, SECONDS_WIDTH)
  const fraction = moment.slice(SECONDS_WIDTH + 1, -1)
  return `${seconds}.${fraction.padEnd(9, '0')}`
}
